import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Lock, ExternalLink, Filter, Grid, List, Download, AlertTriangle, Calendar, FolderKanban } from 'lucide-react';
import SharedUsersPopover from '@/components/SharedUsersPopover';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEMS_PER_PAGE = 9;

const getDomainFavicon = (url) => {
  if (!url) return null;
  try {
    const domain = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch { return null; }
};

const calculateExpiryDays = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const getExpiryStatus = (days) => {
  if (days === null) return null;
  if (days < 0) return { label: 'Expired', color: 'text-red-600', bg: 'bg-red-100' };
  if (days <= 7) return { label: `Expires in ${days} day${days !== 1 ? 's' : ''}`, color: 'text-orange-600', bg: 'bg-orange-100' };
  if (days <= 30) return { label: `Expires in ${days} days`, color: 'text-yellow-600', bg: 'bg-yellow-100' };
  return null;
};

export default function CredentialsList({ user }) {
  const [credentials, setCredentials] = useState([]);
  const [filteredCredentials, setFilteredCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState('All');
  const [viewMode, setViewMode] = useState('card');
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const navigate = useNavigate();

  const fetchFilters = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [catRes, projRes] = await Promise.all([
        axios.get(`${API}/categories`, { headers }),
        axios.get(`${API}/projects`, { headers })
      ]);
      setCategories(catRes.data);
      setProjects(projRes.data);
    } catch (err) {
      // Non-critical: filters will show empty
    }
  }, []);

  const fetchCredentials = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredentials(response.data);
    } catch (error) {
      toast.error('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCredentials();
    fetchFilters();
  }, [fetchCredentials, fetchFilters]);

  // Filter credentials when dependencies change
  useEffect(() => {
    let filtered = credentials;

    if (searchQuery) {
      filtered = filtered.filter(
        (cred) =>
          cred.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          cred.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
          cred.url.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedCategory !== 'All') {
      filtered = filtered.filter((cred) => cred.category === selectedCategory);
    }

    if (selectedProject !== 'All') {
      filtered = filtered.filter((cred) => cred.project_id === selectedProject);
    }

    setFilteredCredentials(filtered);
  }, [credentials, searchQuery, selectedCategory, selectedProject]);

  const exportToExcel = () => {
    const exportData = credentials.map(cred => ({
      Title: cred.title,
      Username: cred.username,
      URL: cred.url,
      Category: cred.category,
      'Created At': new Date(cred.created_at).toLocaleDateString(),
      'Shared': cred.shared_with.length > 0 ? 'Yes' : 'No'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Credentials');
    XLSX.writeFile(wb, `keebox-credentials-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Exported to Excel successfully');
  };

  // Pagination
  const totalPages = Math.ceil(filteredCredentials.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCredentials = filteredCredentials.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-primary font-manrope text-xl">Loading credentials...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight">
            Credentials
          </h1>
          <p className="text-text-secondary mt-2">
            {credentials.length} credential{credentials.length !== 1 ? 's' : ''} stored securely
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportToExcel}
            className="btn-outline flex items-center gap-2"
            data-testid="btn-export-excel"
          >
            <Download size={20} />
            Export
          </button>
          {user?.role !== 'viewer' && (
            <button
              onClick={() => navigate('/dashboard/add')}
              className="btn-primary flex items-center gap-2"
              data-testid="btn-add-credential"
            >
              <Plus size={20} />
              Add Credential
            </button>
          )}
        </div>
      </div>

      <div className="mb-6 flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={20} />
          <input
            type="text"
            placeholder="Search credentials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white border-2 border-gray-200 rounded-lg pl-10 pr-4 py-3 focus:border-primary focus:outline-none text-text-primary"
            data-testid="input-search-credentials"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('card')}
            className={`p-3 rounded-lg transition-colors ${
              viewMode === 'card'
                ? 'bg-primary text-white'
                : 'bg-white border-2 border-gray-200 text-text-primary hover:border-primary'
            }`}
            data-testid="btn-view-card"
          >
            <Grid size={20} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-3 rounded-lg transition-colors ${
              viewMode === 'list'
                ? 'bg-primary text-white'
                : 'bg-white border-2 border-gray-200 text-text-primary hover:border-primary'
            }`}
            data-testid="btn-view-list"
          >
            <List size={20} />
          </button>
        </div>
      </div>

      <div className="mb-6 flex gap-4 flex-wrap items-center">
        <div className="flex gap-2 flex-wrap flex-1">
          <button
            onClick={() => setSelectedCategory('All')}
            className={`px-4 py-2 font-medium rounded-lg transition-colors text-sm ${
              selectedCategory === 'All' ? 'bg-primary text-white' : 'bg-white border-2 border-gray-200 text-text-primary hover:border-primary'
            }`}
            data-testid="filter-category-all"
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.name)}
              className={`px-4 py-2 font-medium rounded-lg transition-colors text-sm ${
                selectedCategory === cat.name ? 'bg-primary text-white' : 'bg-white border-2 border-gray-200 text-text-primary hover:border-primary'
              }`}
              data-testid={`filter-category-${cat.name.toLowerCase()}`}
            >
              {cat.name}
            </button>
          ))}
        </div>
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="bg-white border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-primary focus:outline-none text-text-primary text-sm"
          data-testid="filter-project"
        >
          <option value="All">All Projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {filteredCredentials.length === 0 ? (
        <div className="text-center py-20">
          <Lock className="w-16 h-16 text-text-muted mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
            No credentials found
          </h3>
          <p className="text-text-secondary mb-6">
            {searchQuery || selectedCategory !== 'All'
              ? 'Try adjusting your filters'
              : 'Get started by adding your first credential'}
          </p>
          {user?.role !== 'viewer' && !searchQuery && selectedCategory === 'All' && (
            <button
              onClick={() => navigate('/dashboard/add')}
              className="btn-primary"
              data-testid="btn-add-first-credential"
            >
              Add First Credential
            </button>
          )}
        </div>
      ) : (
        <>
          {viewMode === 'card' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedCredentials.map((cred) => {
                const favicon = getDomainFavicon(cred.url);
                return (
                <div
                  key={cred.id}
                  onClick={() => navigate(`/dashboard/credential/${cred.id}`)}
                  className="bg-white border-2 border-gray-200 p-6 cursor-pointer credential-card hover:shadow-lg"
                  data-testid={`credential-card-${cred.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-primary/10 p-3 rounded-lg overflow-hidden">
                      {favicon ? (
                        <img src={favicon} alt="" className="w-6 h-6" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                      ) : null}
                      <Lock className="w-6 h-6 text-primary" strokeWidth={1.5} style={{ display: favicon ? 'none' : 'block' }} />
                    </div>
                    <SharedUsersPopover sharedWith={cred.shared_with} resourceId={cred.id} resourceType="credential" onUnshare={fetchCredentials} />
                  </div>
                  <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
                    {cred.title}
                  </h3>
                  <p className="text-text-secondary text-sm mb-1">{cred.username}</p>
                  {cred.url && (
                    <div className="flex items-center gap-1 text-primary text-sm mt-2">
                      <ExternalLink size={14} />
                      <span className="truncate">{cred.url}</span>
                    </div>
                  )}
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-text-muted font-medium px-2 py-1 bg-gray-100 rounded-full">
                      {cred.category}
                    </span>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Title</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Username</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Category</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">URL</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Shared</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedCredentials.map((cred) => {
                    const favicon = getDomainFavicon(cred.url);
                    return (
                    <tr
                      key={cred.id}
                      onClick={() => navigate(`/dashboard/credential/${cred.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      data-testid={`credential-row-${cred.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-primary/10 p-2 rounded-lg overflow-hidden">
                            {favicon ? (
                              <img src={favicon} alt="" className="w-4 h-4" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                            ) : null}
                            <Lock className="w-4 h-4 text-primary" style={{ display: favicon ? 'none' : 'block' }} />
                          </div>
                          <span className="font-medium text-text-primary">{cred.title}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-text-secondary">{cred.username}</td>
                      <td className="px-6 py-4">
                        <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-text-muted font-medium">
                          {cred.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-primary text-sm truncate max-w-xs">{cred.url || '-'}</td>
                      <td className="px-6 py-4">
                        <SharedUsersPopover sharedWith={cred.shared_with} resourceId={cred.id} resourceType="credential" onUnshare={fetchCredentials} />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex justify-center items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg border-2 border-gray-200 text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary"
                data-testid="btn-prev-page"
              >
                Previous
              </button>
              <div className="flex gap-2">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                      currentPage === i + 1
                        ? 'bg-primary text-white'
                        : 'bg-white border-2 border-gray-200 text-text-primary hover:border-primary'
                    }`}
                    data-testid={`btn-page-${i + 1}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-lg border-2 border-gray-200 text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary"
                data-testid="btn-next-page"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}